import is from '@sindresorhus/is';
import { useCallback, useContext, useSyncExternalStore } from 'react';

import { ActiveThreadStoreContext } from '../../comments/ActiveThreadStoreProvider';
import { useCommentsQuery } from '../../comments/api/use-comments-query';
import type { CommentClassification } from '../../comments/comment-classification';
import { isCommentsScopeCurrent, useOptionalCommentsScope } from '../../comments/comments-scope-context';
import type { Comment } from '../../data-contract/comment';

export type RegenerateCommentsAvailability = {
    readonly commentCount: number;
    readonly comments: readonly Comment[];
    readonly isAvailable: boolean;
};

const useOptionalCommentClassification = (): {
    readonly hasStore: boolean;
    readonly classification: CommentClassification | null;
} => {
    const store = useContext(ActiveThreadStoreContext);
    const scope = useOptionalCommentsScope();
    const subscribe = useCallback(
        (listener: () => void) => {
            if (is.null(store)) {
                return () => undefined;
            }
            return store.subscribe(listener);
        },
        [store],
    );
    const getSnapshot = useCallback(() => {
        if (is.null(store)) {
            return null;
        }
        const state = store.getState();
        return isCommentsScopeCurrent(state.commentClassificationScope, scope) ? state.commentClassification : null;
    }, [store, scope]);
    const classification = useSyncExternalStore(subscribe, getSnapshot, () => null);

    return { hasStore: !is.null(store), classification };
};

export const useRegenerateCommentsAvailability = (): RegenerateCommentsAvailability => {
    const scope = useOptionalCommentsScope();
    const { classification, hasStore } = useOptionalCommentClassification();
    const { data: comments } = useCommentsQuery({ enabled: !hasStore });
    const queriedVisibleComments = is.array(comments)
        ? comments.filter((comment) => is.nullOrUndefined(comment.parentId) && is.nullOrUndefined(comment.resolution))
        : [];
    const visibleComments = classification?.activeComments ?? queriedVisibleComments;
    const commentCount = visibleComments.length;

    return {
        commentCount,
        comments: visibleComments,
        isAvailable: !is.null(scope) && commentCount > 0,
    };
};
